import React, { useState, useMemo, useEffect } from 'react';
import { Input } from '../components/ui/input-simple';
import { Button } from '../components/ui/button-simple';
import { Search, MapPin, Factory, Eye, AlertCircle, ChevronLeft, ChevronRight, X, Building2, Users, Globe, Phone } from 'lucide-react';
import { municipalities } from '../data/municipalities';
import { processorsAPI } from '../services/api';
import { useProcessors } from '../hooks/useApi';

// Add animations
const styles = `
  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .animate-fade-in-up {
    animation: fadeInUp 0.5s ease-out forwards;
  }

  .animate-fade-in-up:nth-child(1) { animation-delay: 0.1s; }
  .animate-fade-in-up:nth-child(2) { animation-delay: 0.2s; }
  .animate-fade-in-up:nth-child(3) { animation-delay: 0.3s; }
  .animate-fade-in-up:nth-child(4) { animation-delay: 0.4s; }
  .animate-fade-in-up:nth-child(5) { animation-delay: 0.5s; }
  .animate-fade-in-up:nth-child(6) { animation-delay: 0.6s; }

  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .animate-slide-down {
    animation: slideDown 0.6s ease-out forwards;
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  .animate-fade-in {
    animation: fadeIn 0.3s ease-out forwards;
  }

  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translateY(30px) scale(0.95);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }

  .animate-slide-up {
    animation: slideUp 0.4s ease-out forwards;
  }
`;

// Inject styles
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.textContent = styles;
  if (!document.head.querySelector('style[data-user-processors-animations]')) {
    styleSheet.setAttribute('data-user-processors-animations', 'true');
    document.head.appendChild(styleSheet);
  }
}

export function UserProcessorsDirectory() {
  // Use API hook for processors management
  const {
    processors,
    loading,
    error,
    fetchProcessors
  } = useProcessors();

  // Local state for UI
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMunicipality, setSelectedMunicipality] = useState('all');
  const [selectedMarketReach, setSelectedMarketReach] = useState('all');
  const [selectedProcessor, setSelectedProcessor] = useState(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;

  // Load processors on component mount
  useEffect(() => {
    const loadData = async () => {
      try {
        await fetchProcessors(() => processorsAPI.getAll());
      } catch (error) {
      }
    };

    loadData();
  }, [fetchProcessors]);

  // Add anonymous display names for users
  const processorsWithDisplayNames = useMemo(() => {
    return processors.map((processor, index) => ({
      ...processor,
      displayName: `Processor ${index + 1}`,
      displayAddress: processor.address || processor.municipality || 'N/A'
    }));
  }, [processors]);

  // Filter processors
  const filteredProcessors = useMemo(() => {
    return processorsWithDisplayNames.filter(processor => {
      if (processor.id === null || processor.id === undefined) return false;

      const matchesSearch = (processor.displayName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                           (processor.address || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                           (processor.municipality || '').toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesMunicipality = selectedMunicipality === 'all' || 
                                 (processor.municipality || '').toLowerCase() === selectedMunicipality.toLowerCase();
      
      const matchesMarketReach = selectedMarketReach === 'all' || 
                                (processor.marketReach || '').toLowerCase() === selectedMarketReach.toLowerCase();

      return matchesSearch && matchesMunicipality && matchesMarketReach;
    });
  }, [processorsWithDisplayNames, searchTerm, selectedMunicipality, selectedMarketReach]);

  // Paginated processors
  const paginatedProcessors = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredProcessors.slice(startIndex, endIndex);
  }, [filteredProcessors, currentPage, itemsPerPage]);

  // Calculate total pages
  const totalPages = Math.ceil(filteredProcessors.length / itemsPerPage);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedMunicipality, selectedMarketReach]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Modern Header with Gradient */}
        <div className="mb-8 bg-gradient-to-r from-amber-600 to-amber-700 rounded-2xl shadow-xl p-5 text-white animate-slide-down relative z-40">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-white/20 backdrop-blur-sm p-3 rounded-xl">
                <Factory className="h-7 w-7" />
              </div>
              <div>
                <h1 className="text-2xl font-bold mb-1">Cacao Processors Directory</h1>
                <p className="text-amber-50 max-w-2xl text-sm">
                  Browse anonymized profiles of registered cacao processors across Bohol
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-8 border border-gray-200">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
              <Input
                type="text"
                placeholder="Search processors..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 h-12 border-gray-300 focus:border-amber-500 focus:ring-amber-500 rounded-xl"
                aria-label="Search processors"
              />
            </div>

            {/* Municipality Filter */}
            <div className="relative">
              <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5 z-10" />
              <select
                value={selectedMunicipality}
                onChange={(e) => setSelectedMunicipality(e.target.value)}
                className="pl-10 w-full h-12 border border-gray-300 rounded-xl focus:border-amber-500 focus:ring-amber-500 bg-white appearance-none cursor-pointer"
                aria-label="Filter by municipality"
              >
                <option value="all">All Municipalities</option>
                {municipalities.map(municipality => (
                  <option key={municipality} value={municipality}>{municipality}</option>
                ))}
              </select>
            </div>

            {/* Market Reach Filter */}
            <div className="relative">
              <Globe className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5 z-10" />
              <select
                value={selectedMarketReach}
                onChange={(e) => setSelectedMarketReach(e.target.value)}
                className="pl-10 w-full h-12 border border-gray-300 rounded-xl focus:border-amber-500 focus:ring-amber-500 bg-white appearance-none cursor-pointer"
                aria-label="Filter by market reach"
              >
                <option value="all">All Market Reach</option>
                <option value="local">Local</option>
                <option value="regional">Regional</option>
                <option value="national">National</option>
                <option value="international">International</option>
              </select>
            </div>
          </div>

          {/* Results count */}
          <div className="mt-4 flex items-center justify-between text-sm">
            <span className="text-gray-600">
              Showing <span className="font-semibold text-amber-600">{filteredProcessors.length}</span> processors
            </span>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex justify-center items-center py-12">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading processors...</p>
            </div>
          </div>
        )}

        {/* Error State */}
        {error && !loading && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-6">
            <div className="flex items-center">
              <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
              <span className="text-red-800 font-medium">Error loading processors</span>
            </div>
            <p className="text-red-600 mt-2">{error}</p>
            <Button 
              onClick={() => window.location.reload()}
              className="mt-4 bg-red-600 hover:bg-red-700"
            >
              Retry
            </Button>
          </div>
        )}

        {/* Processors Grid */}
        {!loading && !error && filteredProcessors.length > 0 && (
        <>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {paginatedProcessors.map((processor) => (
            <div key={processor.id} className="group bg-white rounded-2xl shadow-md hover:shadow-2xl transition-all duration-300 border border-gray-200 hover:border-amber-300 overflow-hidden flex flex-col animate-fade-in-up" style={{ minHeight: '320px' }}>
              {/* Card Header */}
              <div className="bg-gradient-to-r from-amber-50 to-amber-100 p-3 border-b border-amber-200 flex-shrink-0">
                <h3 className="text-lg text-amber-900 font-bold mb-1 group-hover:text-amber-700 transition-colors">
                  {processor.displayName}
                </h3>
                <div className="flex items-center text-gray-700">
                  <MapPin className="h-4 w-4 mr-1 flex-shrink-0 text-amber-600" />
                  <span className="text-sm">{processor.displayAddress}</span>
                </div>
              </div>
              
              {/* Card Body */}
              <div className="p-3 flex-1 flex flex-col">
                <div className="space-y-2 flex-1">
                  <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <Users className="h-4 w-4 text-blue-600" />
                      <span className="text-sm text-gray-600 font-medium">Owner</span>
                    </div>
                    <span className="font-semibold text-gray-900 text-sm">
                      {processor.ownerOperator || 'N/A'}
                    </span>
                  </div>
                  
                  <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <Building2 className="h-4 w-4 text-green-600" />
                      <span className="text-sm text-gray-600 font-medium">Years Operating</span>
                    </div>
                    <span className="font-semibold text-gray-900 text-sm">
                      {processor.lengthOfBusinessOperationInYears || processor.yearsInOperation || 'N/A'} yrs
                    </span>
                  </div>
                  
                  <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <Globe className="h-4 w-4 text-purple-600" />
                      <span className="text-sm text-gray-600 font-medium">Market Reach</span>
                    </div>
                    <span className="px-2 py-1 rounded-full text-xs font-semibold bg-purple-100 text-purple-800">
                      {processor.marketReach || 'N/A'}
                    </span>
                  </div>
                  
                  <div className="p-2 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-2 mb-1">
                      <MapPin className="h-4 w-4 text-amber-600" />
                      <span className="text-sm text-gray-600 font-medium">Address</span>
                    </div>
                    <p className="text-sm text-gray-900 font-semibold ml-6">
                      {processor.address || 'N/A'}
                    </p>
                  </div>
                  
                  <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <Phone className="h-4 w-4 text-orange-600" />
                      <span className="text-sm text-gray-600 font-medium">Contact</span>
                    </div>
                    <span className="font-semibold text-gray-900 text-sm">
                      {Array.isArray(processor.contactNumbers) 
                        ? processor.contactNumbers[0] 
                        : processor.contact || 'N/A'}
                    </span>
                  </div>
                </div>
                
                {/* Action Button */}
                <div className="mt-3 flex-shrink-0">
                  <Button 
                    className="w-full bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-700 hover:to-amber-800 text-white font-semibold flex items-center justify-center gap-2 rounded-xl shadow-md hover:shadow-lg transition-all py-2"
                    onClick={() => setSelectedProcessor(processor)}
                  >
                    <Eye className="h-4 w-4" />
                    View Profile
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-4 mt-8 bg-white p-6 rounded-2xl shadow-lg">
            <Button
              variant="outline"
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              className="flex items-center gap-2 px-6 rounded-xl hover:bg-amber-50 hover:border-amber-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="h-5 w-5" />
              Previous
            </Button>
            
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Page</span>
              <span className="px-4 py-2 bg-gradient-to-r from-amber-600 to-amber-700 text-white font-bold rounded-xl shadow-md">
                {currentPage}
              </span>
              <span className="text-sm text-gray-600">of {totalPages}</span>
            </div>
            
            <Button
              variant="outline"
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
              className="flex items-center gap-2 px-6 rounded-xl hover:bg-amber-50 hover:border-amber-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
              <ChevronRight className="h-5 w-5" />
            </Button>
          </div>
        )}
        </>
        )}

        {/* Empty State */}
        {!loading && !error && filteredProcessors.length === 0 && (
          <div className="text-center py-12">
            <Factory className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-gray-600 mb-2">No processors found</h3>
            <p className="text-gray-500">Try adjusting your filters to see more results.</p>
          </div>
        )}

        {/* Processor Detail Modal */}
        {selectedProcessor && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 animate-fade-in">
            <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto animate-slide-up">
              <div className="p-6">
                <div className="bg-gradient-to-r from-amber-600 to-amber-700 p-6 rounded-t-2xl">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="bg-white/20 backdrop-blur-sm p-3 rounded-xl">
                        <Building2 className="h-8 w-8 text-white" />
                      </div>
                      <div>
                        <h2 className="text-2xl font-bold text-white">Processor Profile</h2>
                        <p className="text-amber-50 text-sm">Limited public information</p>
                      </div>
                    </div>
                    <Button 
                      variant="ghost" 
                      onClick={() => setSelectedProcessor(null)}
                      className="text-white hover:bg-white/20 rounded-xl h-12 w-12 p-0 transition-all hover:scale-110"
                      aria-label="Close profile"
                    >
                      <X className="h-7 w-7" />
                    </Button>
                  </div>
                </div>
                
                <div className="space-y-6 p-6 max-h-[70vh] overflow-y-auto">
                  {/* Basic Business Information */}
                  <div className="bg-gradient-to-br from-amber-50 to-white p-6 rounded-2xl border border-amber-200 shadow-sm">
                    <div className="flex items-center gap-2 mb-4">
                      <Building2 className="h-5 w-5 text-amber-600" />
                      <h3 className="text-lg font-bold text-amber-900">Business Information</h3>
                    </div>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="bg-white p-3 rounded-xl">
                        <span className="text-sm text-gray-600">Processor Name</span>
                        <p className="font-semibold text-gray-900">{selectedProcessor.displayName}</p>
                      </div>
                      <div className="bg-white p-3 rounded-xl">
                        <span className="text-sm text-gray-600">Owner/Operator</span>
                        <p className="font-semibold text-gray-900">{selectedProcessor.ownerOperator || 'Not specified'}</p>
                      </div>
                      <div className="bg-white p-3 rounded-xl">
                        <span className="text-sm text-gray-600">Years Operating</span>
                        <p className="font-semibold text-gray-900">
                          {selectedProcessor.lengthOfBusinessOperationInYears || selectedProcessor.yearsInOperation || 'Not specified'} years
                        </p>
                      </div>
                      <div className="bg-white p-3 rounded-xl">
                        <span className="text-sm text-gray-600">Market Reach</span>
                        <p className="font-semibold text-gray-900">{selectedProcessor.marketReach || 'Not specified'}</p>
                      </div>
                      <div className="md:col-span-2 bg-white p-3 rounded-xl">
                        <span className="text-sm text-gray-600">Address</span>
                        <p className="font-semibold text-gray-900">{selectedProcessor.address || 'Not specified'}</p>
                      </div>
                      <div className="bg-white p-3 rounded-xl">
                        <span className="text-sm text-gray-600">Contact Number</span>
                        <p className="font-semibold text-gray-900">
                          {Array.isArray(selectedProcessor.contactNumbers) 
                            ? selectedProcessor.contactNumbers.join(', ') 
                            : selectedProcessor.contact || 'Not specified'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
